import type { WasteStatsBucket } from "@/hooks/use-waste-stats";
import type { Lens } from "@/hooks/use-lens";

/**
 * The "When it happens" heatmap (POS2-873) over the stats RPC's `service_dow`
 * buckets — key = '<service>:<isodow>' (isodow 1 = Monday … 7 = Sunday,
 * site-local). Rows are the fixed named services (a zero row is information in
 * a heatmap); 'unassigned' appears only when it has data.
 */
export const HEATMAP_SERVICES = ["breakfast", "lunch", "dinner"] as const;

export type HeatmapRow = { service: string; values: number[] };
export type HeatmapCell = { service: string; isoDow: number };
export type Heatmap = { rows: HeatmapRow[]; max: number; hot: HeatmapCell | null };

/** Matrix in the current lens: fixed service rows × 7 dow columns, the visible
 * max (intensity denominator), and the hot cell (highest value; null when the
 * period is empty in this lens). */
export function heatmapMatrix(buckets: WasteStatsBucket[], lens: Lens): Heatmap {
  const rows = new Map<string, number[]>(HEATMAP_SERVICES.map((s) => [s, Array(7).fill(0)]));
  for (const bucket of buckets) {
    const sep = bucket.key.lastIndexOf(":");
    const service = bucket.key.slice(0, sep);
    const isoDow = Number(bucket.key.slice(sep + 1));
    const value = lens === "weight" ? bucket.total_weight : bucket.total_cost;
    if (!rows.has(service)) rows.set(service, Array(7).fill(0));
    rows.get(service)![isoDow - 1] += value;
  }
  // Unassigned (or any unexpected service) rows only earn their place with data.
  const out: HeatmapRow[] = [...rows.entries()]
    .filter(([service, values]) => isNamedService(service) || values.some((v) => v > 0))
    .map(([service, values]) => ({ service, values }));

  let max = 0;
  let hot: HeatmapCell | null = null;
  for (const row of out) {
    row.values.forEach((value, i) => {
      if (value > max) {
        max = value;
        hot = { service: row.service, isoDow: i + 1 };
      }
    });
  }
  return { rows: out, max, hot };
}

function isNamedService(service: string): boolean {
  return (HEATMAP_SERVICES as readonly string[]).includes(service);
}

/** Blue intensity for a cell, normalised to the visible max (design mock §8). */
export function heatAlpha(value: number, max: number): number {
  const t = max > 0 ? value / max : 0;
  return 0.06 + t * 0.55;
}

const ISO_DOW: Record<string, number> = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 };

/** ISO day-of-week (1 = Monday … 7 = Sunday) of an instant in a zone — the
 * client-side twin of the RPC's site-local bucketing (the event's `timezone`
 * field resolves through the same kitchen → site → account → org chain). */
export function isoDowInTz(isoTimestamp: string, timezone: string | null): number {
  // Machine formatter — never locale-aware (POS2-1134). The short weekday is looked up in the
  // English `ISO_DOW` map below, not shown to anyone: under `de` this yields "Mo", misses the map,
  // and throws — taking the heatmap and `lib/proof.ts`'s filter down with it. Nothing here may
  // import `lib/format.ts`.
  const weekday = new Intl.DateTimeFormat("en-GB", {
    weekday: "short",
    timeZone: timezone ?? "UTC",
  }).format(new Date(isoTimestamp));
  const dow = ISO_DOW[weekday];
  if (!dow) throw new Error(`Unexpected weekday "${weekday}"`);
  return dow;
}
